import PageLayout from '@/components/PageLayout';
import { Box, Button, Card, CardContent, Chip, Grid, Link, Stack, Typography, useTheme } from '@mui/material';
import { WithTeamPermissionCheck } from '@/hocs/WithTeamPermissionCheck';
import WorkspaceApiManager, { WorkspaceApiManagerProps } from '@/components/WorkspaceApiManager';
import DashboardProfilesStatus from '@/components/dashboard/DashboardProfileStatus';
import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GetApiClientDocument } from '@/graphql/generated/getApiClient.generated';
import { CreateApiClientDocument } from '@/graphql/generated/createApiClient.generated';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import {
    CodeRounded,
    DescriptionRounded,
    IntegrationInstructionsRounded,
    ManageAccountsRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DashboardLatestPosts from '@/components/dashboard/swonkie-dev/DashboardLatestPosts';
import DashboardCompetitorsList from '@/components/dashboard/swonkie-dev/DashboardCompetitorsList';
import { API_DOCS_URL } from '@/constants';
import { PostsFiltersProvider } from '@/components/post-planning/filters/PostsFiltersProvider';
import { PostsProvider } from '@/components/post-planning/providers/PostsProvider';
import { PostsViewType } from '@/graphql/generated/types.generated';

// Self-contained wrapper so DashboardDev can embed WorkspaceApiManager without prop-drilling
const WorkspaceApiManagerWidget = () => {
    const { t } = useTranslation();
    const [hasApiAccess, setHasApiAccess] = useState(true);
    const [createdKeys, setCreatedKeys] = useState<WorkspaceApiManagerProps['createdKeys']>(null);
    const [rotatedKey, setRotatedKey] = useState<WorkspaceApiManagerProps['rotatedKey']>(null);

    const { data, loading, refetch } = useQuery(GetApiClientDocument, {
        onError: (error) => {
            if (error.message === 'public-api.not-available') {
                setHasApiAccess(false);
            } else {
                enqueueSnackbar(t(error.message));
            }
        },
    });

    const [createApiClient, { loading: creating }] = useMutation(CreateApiClientDocument, {
        onCompleted: (data) => {
            const created = data?.createApiClient;
            if (created?.primaryKey && created?.secondaryKey) {
                setCreatedKeys({ primaryKey: created.primaryKey, secondaryKey: created.secondaryKey });
            }
            refetch();
        },
        onError: (error) => enqueueSnackbar(t(error.message)),
    });

    return (
        <WorkspaceApiManager
            apiClient={data?.getApiClient}
            loading={loading}
            hasApiAccess={hasApiAccess}
            createdKeys={createdKeys}
            rotatedKey={rotatedKey}
            creating={creating}
            onCreateApiClient={() => createApiClient()}
            onRotateSuccess={(slot, key, generatedAt) => setRotatedKey({ slot, key, generatedAt })}
        />
    );
};

function DashboardDev() {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <PageLayout>
            <PostsFiltersProvider>
                <PostsProvider viewType={PostsViewType.Calendar}>
                    <Stack direction="column" alignItems="flex-start" justifyContent="start" spacing={3}>
                        {/* Header Section */}
                        <Stack gap={1} width="100%">
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                                gap={2}
                            >
                                <Stack gap={0.5}>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Typography variant="h4">{t('dashboard.dev.title')}</Typography>
                                        <Chip
                                            label="API"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('dashboard.dev.subtitle')}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" gap={1}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ManageAccountsRounded />}
                                        onClick={() => navigate('/settings/workspace/social-profile')}
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {t('dashboard.dev.manage-profiles')}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>

                        {/* Quick Links Section */}
                        <Stack direction="row" gap={2} flexWrap="wrap">
                            <Card
                                component={Link}
                                href={API_DOCS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    textDecoration: 'none',
                                    width: 'fit-content',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: theme.shadows[8],
                                    },
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" alignItems="center" gap={2}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: 'primary.main',
                                                color: 'primary.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: 56,
                                                minHeight: 56,
                                            }}
                                        >
                                            <IntegrationInstructionsRounded sx={{ fontSize: 28 }} />
                                        </Box>
                                        <Stack>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {t('dashboard.dev.api-reference')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('dashboard.dev.api-reference.desc')}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                            <Card
                                component={Link}
                                href={`${API_DOCS_URL}quick-start`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    textDecoration: 'none',
                                    width: 'fit-content',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: theme.shadows[8],
                                    },
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" alignItems="center" gap={2}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: 'success.main',
                                                color: 'success.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: 56,
                                                minHeight: 56,
                                            }}
                                        >
                                            <CodeRounded sx={{ fontSize: 28 }} />
                                        </Box>
                                        <Stack>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {t('dashboard.dev.getting-started')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('dashboard.dev.getting-started.desc')}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                            <Card
                                component={Link}
                                href={`${API_DOCS_URL}examples`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    textDecoration: 'none',
                                    width: 'fit-content',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: theme.shadows[8],
                                    },
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" alignItems="center" gap={2}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: 'warning.main',
                                                color: 'warning.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: 56,
                                                minHeight: 56,
                                            }}
                                        >
                                            <DescriptionRounded sx={{ fontSize: 28 }} />
                                        </Box>
                                        <Stack>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {t('dashboard.dev.code-examples')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('dashboard.dev.code-examples.desc')}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>

                        {/* API Credentials + Profiles Status */}
                        <Grid container spacing={2} width="100%" alignItems="stretch">
                            <Grid size={{ xs: 12, xl: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <WorkspaceApiManagerWidget />
                            </Grid>
                            <Grid size={{ xs: 12, xl: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <DashboardProfilesStatus />
                            </Grid>
                        </Grid>

                        {/* Competitors + Latest Posts */}
                        <Grid container spacing={2} width="100%">
                            <Grid size={{ xs: 12, xl: 6 }}>
                                <DashboardCompetitorsList />
                            </Grid>
                            <Grid size={{ xs: 12, xl: 6 }}>
                                <DashboardLatestPosts />
                            </Grid>
                        </Grid>
                    </Stack>
                </PostsProvider>
            </PostsFiltersProvider>
        </PageLayout>
    );
}

export default WithTeamPermissionCheck(DashboardDev);
